import { useCallback } from "react";

import type { ForNodeType } from "@/components/features/_model/types";

type Props = {
	capacityAdultMaximum: ForNodeType<number>;
	capacityKidsMaximum: ForNodeType<number>;
	capacityPeopleMaximum: ForNodeType<number>;
	isKidsUnavailable: ForNodeType<boolean>;

	onSetViewModel: (
		values: {
			key: "capacityAdultMaximum" | "capacityKidsMaximum" | "capacityPeopleMaximum" | "isKidsUnavailable";
			value: ForNodeType<number> | ForNodeType<boolean>;
		}[],
	) => void;
};

export const useRoomCapacity = ({
	capacityAdultMaximum,
	capacityKidsMaximum,
	capacityPeopleMaximum,
	isKidsUnavailable,
	onSetViewModel,
}: Props) => {
	const onSetCapacityPeopleMaximum = useCallback(
		(input: number | undefined) => {
			onSetViewModel([
				{
					key: "capacityPeopleMaximum",
					value: { ...capacityPeopleMaximum, value: input ?? 0 },
				},
			]);
		},
		[capacityPeopleMaximum, onSetViewModel],
	);

	const onSetCapacityAdultMaximum = useCallback(
		(input: number | undefined) => {
			onSetViewModel([{ key: "capacityAdultMaximum", value: { ...capacityAdultMaximum, value: input ?? 0 } }]);
		},
		[capacityAdultMaximum, onSetViewModel],
	);

	const onSetCapacityKidsMaximum = useCallback(
		(input: number | undefined) => {
			onSetViewModel([{ key: "capacityKidsMaximum", value: { ...capacityKidsMaximum, value: input ?? 0 } }]);
		},
		[capacityKidsMaximum, onSetViewModel],
	);

	const onSetIsKidsUnavailable = useCallback(
		(input: boolean) => {
			if (input === true && capacityPeopleMaximum.value > 0) {
				// 子供の利用が不可能になった場合、子供の最大利用人数を0にして、定員を大人の最大利用人数にする
				onSetViewModel([
					{ key: "isKidsUnavailable", value: { ...isKidsUnavailable, value: input } }, //
					{
						key: "capacityAdultMaximum",
						value: { ...capacityAdultMaximum, value: capacityPeopleMaximum.value },
					},
					{ key: "capacityKidsMaximum", value: { ...capacityKidsMaximum, value: 0 } },
				]);
			} else {
				onSetViewModel([{ key: "isKidsUnavailable", value: { ...isKidsUnavailable, value: input } }]);
			}
		},
		[capacityAdultMaximum, capacityKidsMaximum, capacityPeopleMaximum.value, isKidsUnavailable, onSetViewModel],
	);

	const isEdited =
		capacityPeopleMaximum?.isEdited ||
		capacityAdultMaximum?.isEdited ||
		capacityKidsMaximum?.isEdited ||
		isKidsUnavailable.isEdited;
	const hasError = Boolean(
		capacityPeopleMaximum?.error ||
			capacityAdultMaximum?.error ||
			capacityKidsMaximum?.error ||
			isKidsUnavailable.error,
	);

	return {
		isEdited,
		hasError,

		capacityPeopleMaximum,
		onSetCapacityPeopleMaximum,

		capacityAdultMaximum,
		onSetCapacityAdultMaximum,

		capacityKidsMaximum,
		onSetCapacityKidsMaximum,

		isKidsUnavailable,
		onSetIsKidsUnavailable,
	};
};
